import { useAuthStore } from '../../stores/authStore';
import { NAVINA_EMAIL_KEY } from '../../utils/consts';
import {
	embedZendeskScriptOnce,
	getZendesk,
	Positions,
	TicketForms,
	ZendeskModel,
	type ZendeskSettings,
	type Zendesk,
} from './ZendeskModel';
import { observer } from 'mobx-react-lite';
import { type ComponentProps, useEffect, useState } from 'react';

interface SubmitFeedbackGlobalFeatureProps extends ComponentProps<'div'> {
	readonly customFields?: ZendeskSettings['customFields'];
}

export const SubmitFeedbackGlobalFeature = observer(function SubmitFeedbackGlobalFeature({
	children,
	customFields: outerCustomFields = [] as const,
	...props
}: SubmitFeedbackGlobalFeatureProps) {
	const authStore = useAuthStore();
	const { userFullName } = authStore;
	const userEmail = localStorage.getItem(NAVINA_EMAIL_KEY) || authStore.getEmail;

	const [zendesk, setZendesk] = useState<{ readonly instance: Zendesk } | null>(null);
	const zendeskInstance = zendesk && zendesk.instance;

	useEffect(function getZendeskInstance() {
		getZendesk().then(function updateZendeskInstance(instance): void {
			setZendesk({ instance });
		});
	}, []);

	useEffect(function loadZendeskScript() {
		embedZendeskScriptOnce();
	}, []);

	if (!zendeskInstance) {
		return null;
	}

	const customFields = [
		{ id: 'name', defaultValue: userFullName },
		{ id: 'email', defaultValue: userEmail },
		...outerCustomFields,
	] as const satisfies ZendeskSettings['customFields'];

	const zendeskModel = ZendeskModel.create({ zendeskInstance: zendeskInstance });
	zendeskModel.initialize(
		{
			horizontalPosition: Positions.Right,
			verticalPosition: Positions.Bottom,
			offsetX: 0,
			offsetY: 0,
			formTitle: 'Contact Us',
			attachments: true,
			ticketForms: [TicketForms.ContactUs],
			customFields,
		} as const satisfies ZendeskSettings,
		Object.values(customFields)
			.filter(function isFieldWithDefaultValue(field): boolean {
				return Boolean(field.defaultValue);
			})
			.map(function getCustomFieldId(field): string {
				return field.id;
			}),
	);

	return (
		<div
			{...{
				onClick: zendeskModel.open,
				...props,
			}}
		>
			{children}
		</div>
	);
});
